'use client';

import React, { useMemo } from 'react';
import { ArrowRight, Car, Cog, Luggage, Star, Users } from 'lucide-react';
import type { SectionProps } from '@/lib/storefront/section-templates';
import { safeHref } from '@/lib/storefront/safe-url';
import { useStorefrontData } from '@/lib/storefront/storefront-data';
import { useCurrency } from '@/components/site/CurrencyContext';
import { apiAsset } from '@/config';
import type { Vehicle } from '@/types';
import { containerClass, sectionStyle } from './HeaderSections';

const COLUMN_CLASS: Record<number, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

function clampLimit(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 3;
  return Math.max(1, Math.min(12, Math.trunc(parsed)));
}

function visibleVehicles(vehicles: Vehicle[], category: string, limit: number): Vehicle[] {
  const filtered = category && category !== 'All'
    ? vehicles.filter((vehicle) => vehicle.category === category)
    : vehicles;

  return [...filtered]
    .sort((left, right) => {
      const highlighted = Number(Boolean(right.highlight)) - Number(Boolean(left.highlight));
      if (highlighted !== 0) return highlighted;
      return Number(right.rating || 0) - Number(left.rating || 0);
    })
    .slice(0, limit);
}

function FleetCard({
  vehicle,
  showPrices,
  showRating,
}: {
  vehicle: Vehicle;
  showPrices: boolean;
  showRating: boolean;
}) {
  const { fmt } = useCurrency();
  const image = apiAsset(vehicle.imageUrl ?? '');
  const rating = Number(vehicle.rating || 0);

  return (
    <a
      href={`/vehicles/${encodeURIComponent(vehicle.id)}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {image ? (
          <img
            src={image}
            alt={vehicle.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-300">
            <Car className="h-12 w-12" />
          </div>
        )}
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 shadow-sm">
          {vehicle.category}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-slate-900">{vehicle.name}</h3>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}
            </p>
          </div>
          {showRating && rating > 0 && (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-amber-600">
              <Star className="h-3.5 w-3.5 fill-current" />
              {rating.toFixed(1)}
            </span>
          )}
        </div>

        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" /> {vehicle.seats} seats
          </span>
          <span className="inline-flex items-center gap-1">
            <Cog className="h-3.5 w-3.5" /> {vehicle.transmission}
          </span>
          <span className="inline-flex items-center gap-1">
            <Luggage className="h-3.5 w-3.5" /> {vehicle.bags} bags
          </span>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-4">
          {showPrices ? (
            <p className="text-sm text-slate-500">
              <span className="text-lg font-bold text-slate-900">{fmt(vehicle.pricePerDay)}</span>
              {' '}/ day
            </p>
          ) : (
            <span />
          )}
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand">
            View details <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </a>
  );
}

function FleetCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="aspect-[4/3] animate-pulse bg-slate-100" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-3/4 animate-pulse rounded bg-slate-100" />
        <div className="h-5 w-1/3 animate-pulse rounded bg-slate-100" />
      </div>
    </div>
  );
}

export function FleetGrid({ content, style }: SectionProps) {
  const { vehicles, vehiclesReady } = useStorefrontData();
  const {
    eyebrow = 'Our fleet',
    headline = 'Cars for every trip',
    subheadline = 'A well-maintained fleet, transparent daily rates and a team that answers.',
    category = 'All',
    limit = 3,
    columns = 3,
    showPrices = true,
    showRating = true,
    ctaText = 'View all vehicles',
    ctaHref = '/vehicles',
    emptyText = 'Vehicles will appear here once the fleet is published.',
  } = content;

  const max = clampLimit(limit);
  const columnCount = COLUMN_CLASS[Number(columns)] ? Number(columns) : 3;
  const cols = COLUMN_CLASS[columnCount];
  const filtered = useMemo(
    () => visibleVehicles(vehicles, String(category), max),
    [vehicles, category, max],
  );

  const showSkeleton = !vehiclesReady;
  const skeletonCount = Math.min(Math.max(max, 3), 6);

  return (
    <section className="w-full bg-white" style={sectionStyle(style)}>
      <div className={containerClass(style)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            {eyebrow && (
              <span className="text-xs font-semibold uppercase tracking-wider text-brand">
                {eyebrow}
              </span>
            )}
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              {headline}
            </h2>
            {subheadline && (
              <p className="mt-3 text-base text-slate-600">{subheadline}</p>
            )}
          </div>
          {ctaText && ctaHref && (
            <a
              href={safeHref(ctaHref)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700"
            >
              {ctaText} <ArrowRight className="h-4 w-4" />
            </a>
          )}
        </div>

        <div className={`mt-8 grid gap-5 ${cols}`}>
          {showSkeleton ? (
            Array.from({ length: skeletonCount }).map((_, index) => (
              <FleetCardSkeleton key={index} />
            ))
          ) : filtered.length > 0 ? (
            filtered.map((vehicle) => (
              <FleetCard
                key={vehicle.id}
                vehicle={vehicle}
                showPrices={Boolean(showPrices)}
                showRating={Boolean(showRating)}
              />
            ))
          ) : (
            <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
              <Car className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">{emptyText}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export const FLEET_VARIATIONS = {
  featured: FleetGrid,
  full: FleetGrid,
} as const;
